'use client';

import type { FeatureAttribute } from '@magicschool/business-logic/feature-flags';
import type { Tables } from '@magicschool/supabase/types';
import { useEffect, useState } from 'react';
import { PageLoader } from '@/components/PageLoader';
import { useStore } from '@/features/store';
import { useDeepCompareMemoize } from '@/hooks/useDeepCompareMemoize';

type StudentRoomHydrationClientProps = {
  room: Tables<'rooms'>;
  roomStudent: Tables<'room_students'>;
  orgAttributes: FeatureAttribute[];
  children: React.ReactNode;
};

/**
 * Hydrates the student store with room data and the room creator's org_attributes.
 * This enables scheduling-based state transitions (e.g., auto-unlock when scheduled
 * start time arrives) and lets student-path hooks read the creator org's attributes
 * directly — applyOrgAttributes never runs for students (no user id), so the auth
 * slice's orgAttributes is the only way an org lever reaches them.
 * Shows a loading state until hydration is complete to prevent UI flashes.
 */
export const StudentRoomHydrationClient = ({ children, roomStudent, room, orgAttributes }: StudentRoomHydrationClientProps) => {
  const load = useStore((s) => s.StudentStoreData.load, []);
  const setAuthField = useStore((s) => s.AuthStoreData.setField, []);
  const [isHydrated, setIsHydrated] = useState(false);

  const stableRoom = useDeepCompareMemoize(room);
  const stableRoomStudent = useDeepCompareMemoize(roomStudent);
  const stableOrgAttributes = useDeepCompareMemoize(orgAttributes);

  useEffect(() => {
    let cancelled = false;
    const loadRoom = async () => {
      setAuthField('orgAttributes')(stableOrgAttributes);
      await load({ room: stableRoom, roomStudent: stableRoomStudent });
      if (!cancelled) setIsHydrated(true);
    };
    loadRoom();
    return () => {
      cancelled = true;
    };
  }, [load, setAuthField, stableRoom, stableRoomStudent, stableOrgAttributes]);

  if (!isHydrated) {
    return <PageLoader />;
  }

  return children;
};
